"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-orange-500/40 bg-orange-500/5 p-8 rounded-lg">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3">
          [ STATUS / ONTVANGEN ]
        </p>
        <p className="font-default text-2xl text-white mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest border-white/30 text-white hover:bg-white hover:text-black rounded-lg">
          Nieuwe aanvraag
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">
              Volledige Naam
            </FormLabel>
            <FormControl>
              <Input placeholder="Naam Achternaam" className="bg-transparent border-0 border-b-2 border-gray-700 px-0 h-12 text-base font-default text-white placeholder:text-gray-600 focus-visible:border-orange-500 focus-visible:ring-0 transition-all" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">
              E-mailadres
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="naam@bedrijf.nl" className="bg-transparent border-0 border-b-2 border-gray-700 px-0 h-12 text-base font-default text-white placeholder:text-gray-600 focus-visible:border-orange-500 focus-visible:ring-0 transition-all" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">
              Telefoonnummer
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+31 6 12345678" className="bg-transparent border-0 border-b-2 border-gray-700 px-0 h-12 text-base font-default text-white placeholder:text-gray-600 focus-visible:border-orange-500 focus-visible:ring-0 transition-all" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="service_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">
              Type Dienst
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b-2 border-gray-700 px-0 h-12 text-base font-default text-white hover:bg-transparent focus-visible:border-orange-500 focus-visible:ring-0 [&>svg]:text-orange-500 transition-all w-full">
                  <SelectValue placeholder="Selecteer een dienst" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border border-gray-700">
                  <SelectItem value="Social Media" className="font-default">Social Media Strategie</SelectItem>
                  <SelectItem value="Content Creatie" className="font-default">Content Creatie</SelectItem>
                  <SelectItem value="Branding" className="font-default">Branding & Design</SelectItem>
                  <SelectItem value="Groei" className="font-default">Groei & Advertenties</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">
              Jouw Doelen
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Vertel ons over je ambities..." rows={4} className="bg-transparent border-0 border-b-2 border-gray-700 px-0 py-2 text-base font-default text-white placeholder:text-gray-600 focus-visible:border-orange-500 focus-visible:ring-0 transition-all resize-none" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="terms_acknowledgement" render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-4 pt-2">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="size-5 border border-gray-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 mt-0.5 rounded-lg" />
              </FormControl>
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400 leading-relaxed cursor-pointer">
                Ik ga akkoord met de voorwaarden
              </FormLabel>
            </div>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="border border-red-500/40 bg-red-500/10 p-4 rounded-lg">
          <p className="font-mono text-xs uppercase tracking-widest text-red-500">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 bg-orange-500 text-black font-default font-bold uppercase tracking-[0.15em] text-sm rounded-lg hover:bg-white transition-all group">
        <span>{isSubmitting ? "Verzenden..." : "Start de Groei"}</span>
        <ArrowRight className="size-5 ml-2 group-hover:translate-x-2 transition-transform" />
      </Button>

      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 pt-2">
        [ Data wordt veilig verwerkt. Reactie binnen 48 uur. ]
      </p>
    </Form>;
}
export default function Groei() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}